import { AxiosInstance } from "axios";
import ApiClient from "./ApiClient";
import {
  AddMemberRequest,
  UpdateMemberRequest,
  OrganizationMemberDTO,
  Role,
} from "../types/OrganizationMember";

// Base URL for the API
const API_URL = process.env.REACT_APP_MENTORING_API_URL || "";

/**
 * OrganizationMemberService provides methods to interact with the Organization Members API endpoints.
 * All methods return Promises that resolve to the response data or throw an error.
 */
class OrganizationMemberService {
  private apiClient: AxiosInstance;

  constructor() {
    this.apiClient = new ApiClient(API_URL).getClient();
  }

  /**
   * List all members in an organization.
   * @param organizationId The ID of the organization.
   * @returns Promise resolving to an array of OrganizationMemberListDTO.
   */
  public listMembers = async (
    organizationId: string
  ): Promise<OrganizationMemberDTO[]> => {
    const response = await this.apiClient.get<OrganizationMemberDTO[]>(
      `/organizations/${organizationId}/members`
    );
    return response.data;
  };

  /**
   * Add a member to an organization.
   * @param organizationId The ID of the organization.
   * @param memberData The member data to add.
   * @returns Promise resolving to the newly created OrganizationMemberDTO.
   */
  public addMember = async (
    organizationId: string,
    memberData: AddMemberRequest
  ): Promise<OrganizationMemberDTO> => {
    const response = await this.apiClient.post<OrganizationMemberDTO>(
      `/organizations/${organizationId}/members`,
      memberData
    );
    return response.data;
  };

  /**
   * Update a member's details in an organization.
   * @param organizationId The ID of the organization.
   * @param userId The ID of the user to update.
   * @param updateData The data to update (role, first_name, last_name).
   * @returns Promise resolving to the updated OrganizationMemberDTO.
   */
  public updateMember = async (
    organizationId: string,
    userId: string,
    updateData: UpdateMemberRequest
  ): Promise<OrganizationMemberDTO> => {
    const response = await this.apiClient.patch<OrganizationMemberDTO>(
      `/organizations/${organizationId}/members/${userId}`,
      updateData
    );
    return response.data;
  };

  /**
   * Get a single member by user ID.
   * @param organizationId The ID of the organization.
   * @param userId The ID of the user.
   * @returns Promise resolving to the OrganizationMemberDTO.
   */
  public getMember = async (organizationId: string, userId: string): Promise<OrganizationMemberDTO> => {
    const response = await this.apiClient.get<OrganizationMemberDTO>(`/organizations/${organizationId}/members/${userId}`);
    return response.data;
  }

  /**
   * Remove a member from an organization.
   * @param organizationId The ID of the organization.
   * @param userId The ID of the user to remove.
   * @returns Promise that resolves when the member is removed.
   */
  public removeMember = async (
    organizationId: string,
    userId: string
  ): Promise<void> => {
    await this.apiClient.delete(
      `/organizations/${organizationId}/members/${userId}`
    );
  };

  // Export Role type for convenience
  public static Role = {
    ADMIN: "ADMIN" as Role,
    MEMBER: "MEMBER" as Role,
  };
}

const organizationMemberService = new OrganizationMemberService();
export default organizationMemberService;